import { useQueryClient } from '@tanstack/react-query';

import { formatDate, formatNumber, toMachineDate } from '@shared/format';
import {
  DTable,
  DTableBody,
  DTableHead,
  DTableRowHeader,
  DTableScroll,
  RowSkeleton,
} from '@shared/ui/dtable';
import { EmptyState } from '@shared/ui/EmptyState';
import { RowMenu, type RowMenuEntry } from '@shared/ui/rowmenu';
import { StateBadge } from '@shared/ui/StateBadge';
import { SurfaceError } from '@shared/ui/SurfaceError';

import { copy } from './copy';
import { errors } from './errors';
import { configurationsKey, useConfigurations } from './queries';
import type { ServiceSectionSpec } from './services';
import type { ConfigurationRow } from './types';

const TABLE_COLUMNS = 4;

/**
 * One service's configuration table.
 *
 * **ONE SERVICE PER PAGE, AND THAT IS WHY THIS COMPONENT NO LONGER DRAWS A
 * HEADING.** `/settings` used to render all nine sections at once: on develop
 * that measured 23 653 px of page holding 240 rows across eight tables, and the
 * only way to reach a key was to scroll past every key above it. Each service
 * now has its own route, the page's `<h1>` names it, and a second heading here
 * would state the same thing twice.
 *
 * **The read is still its own error boundary**, and it still matters: the page
 * draws the strip and the head whatever the read does, so a service that is
 * unreachable produces a banner inside a working page rather than a blank one.
 *
 * **Three row states, and all three come from the service.** `is_editable` and
 * `is_secret` are data the owning service publishes rather than a list this
 * console maintains:
 *
 * - **editable** — the row menu offers one entry.
 * - **read-only** — the actions cell is **empty**, not a disabled control. The
 *   rule that a disabled control must never be hidden governs controls that
 *   exist; here there is no action to disable, and a key the service must own at
 *   boot is shown with its value so an operator can read what governs the
 *   platform and be told why it cannot be changed here.
 * - **secret** — no value is rendered in any form. The payload carries no
 *   `value` field for it at all, the cell shows fixed masking dots with a label
 *   so twelve bullets are never read out one by one, and the menu offers the
 *   description and rotation.
 *
 * **Every value renders Latin, in the mono face, isolated.** A configuration
 * value is a string a service parses, not a quantity a person reads — the
 * visible inconsistency between `120` here and `٤٬١٦٥` on a dashboard two routes
 * away is the correct one, and a surface that rendered both the same way would
 * be wrong about one of them.
 */
export function ConfigurationTableSection({
  spec,
  onEdit,
  onRotate,
}: {
  spec: ServiceSectionSpec;
  onEdit: (row: ConfigurationRow) => void;
  onRotate: (row: ConfigurationRow) => void;
}) {
  const query = useConfigurations(spec.key);
  const queryClient = useQueryClient();

  const rows = query.data ?? [];

  return (
    <section aria-labelledby={spec.id}>
      <h2 id={spec.id} className="sr-only">
        {spec.heading}
      </h2>

      <p className="mb-4 max-w-measure text-xs text-ink-muted">
        <bdi className="mono">{spec.table}</bdi> — {spec.context}
      </p>

      {/* Counted from the rows that drew, so the figure and the table agree. */}
      {query.data && rows.length > 0 ? (
        <p className="mb-4 max-w-measure text-xs text-ink-muted">
          {copy.service.count(formatNumber(rows.length))}
        </p>
      ) : null}

      {query.isError ? (
        <SurfaceError
          error={query.error}
          catalog={errors}
          onRetry={() => void queryClient.invalidateQueries({ queryKey: configurationsKey(spec.key) })}
        />
      ) : null}

      {query.isPending ? (
        <>
          <p role="status" className="sr-only">
            {copy.states.loading}
          </p>
          <ConfigurationTable caption={spec.caption}>
            <RowSkeleton columns={TABLE_COLUMNS} />
          </ConfigurationTable>
        </>
      ) : null}

      {query.data ? (
        rows.length === 0 ? (
          <EmptyState message={copy.states.empty} />
        ) : (
          <ConfigurationTable caption={spec.caption}>
            {rows.map((row) => (
              <ConfigurationTableRow
                key={row.key}
                row={row}
                onEdit={() => onEdit(row)}
                onRotate={() => onRotate(row)}
              />
            ))}
          </ConfigurationTable>
        )
      ) : null}
    </section>
  );
}

function ConfigurationTableRow({
  row,
  onEdit,
  onRotate,
}: {
  row: ConfigurationRow;
  onEdit: () => void;
  onRotate: () => void;
}) {
  const entries: RowMenuEntry[] = [];

  if (row.is_secret) {
    entries.push(
      { key: `${row.key}:describe`, label: copy.rowMenu.editSecret, onSelect: onEdit },
      // Rotation voids the standing value and nothing restores it, so it takes
      // the danger weight inside the panel.
      { key: `${row.key}:rotate`, label: copy.rowMenu.rotate, danger: true, onSelect: onRotate },
    );
  } else if (row.is_editable) {
    entries.push({ key: `${row.key}:edit`, label: copy.rowMenu.edit, onSelect: onEdit });
  }

  return (
    <tr>
      <DTableRowHeader>
        <span className="mono block">
          <bdi>{row.key}</bdi>
        </span>
        {row.is_secret ? (
          <span className="mt-1 block">
            <StateBadge kind="neutral">{copy.badges.secret}</StateBadge>
          </span>
        ) : null}
        {/*
          Neutral rather than warning: the warning kind is reserved for a
          transitional state, and this is not a state that passes — it is the
          key's own nature.
        */}
        {!row.is_secret && !row.is_editable ? (
          <span className="mt-1 block">
            <StateBadge kind="neutral">{copy.badges.readonly}</StateBadge>
          </span>
        ) : null}
      </DTableRowHeader>

      <td className="max-w-[16rem] [overflow-wrap:anywhere] lg:max-w-[24rem]">
        {row.is_secret ? (
          <>
            <bdi className="mono block" aria-label={copy.mask}>
              <span aria-hidden="true">{copy.mask}</span>
              <span className="sr-only">{copy.maskLabel}</span>
            </bdi>
            <span className="mt-1 block text-xs text-ink-muted">
              {row.rotated_at ? (
                <>
                  {copy.rotatedAt}{' '}
                  <time dateTime={toMachineDate(row.rotated_at)}>
                    <bdi>{formatDate(row.rotated_at)}</bdi>
                  </time>
                </>
              ) : (
                copy.neverRotated
              )}
            </span>
          </>
        ) : (
          <bdi className="mono">{row.value}</bdi>
        )}
      </td>

      <td className="text-ink-muted">{row.description}</td>

      {/* Empty on a read-only key: there is no action to disable. */}
      <td className="w-[3.5rem]">
        {entries.length > 0 ? <RowMenu label={copy.rowMenu.label(row.key)} entries={entries} /> : null}
      </td>
    </tr>
  );
}

/**
 * The widest minimum in the area, and it is a reading-measure decision: three of
 * the four columns are long text by nature — a key that does not break, a value
 * that may be a list of eight events, and a description that is a full sentence.
 * The table scrolls inside its own wrapper and the page body never does.
 */
function ConfigurationTable({ caption, children }: { caption: string; children: React.ReactNode }) {
  return (
    <DTableScroll>
      <DTable caption={caption} minInlineSize="48rem">
        <DTableHead>
          <tr>
            <th scope="col">{copy.columns.key}</th>
            <th scope="col">{copy.columns.value}</th>
            <th scope="col">{copy.columns.description}</th>
            <th scope="col">
              <span className="sr-only">{copy.columns.actions}</span>
            </th>
          </tr>
        </DTableHead>
        <DTableBody>{children}</DTableBody>
      </DTable>
    </DTableScroll>
  );
}
